import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Box,
  Tag,
  TagLabel,
  Tooltip,
  HStack
} from '@chakra-ui/react'
import { format, isValid, parseISO } from 'date-fns'
import { FC, ReactElement } from 'react'
import ReactPaginate from 'react-paginate'

import LayoutEmpty from '@component/Layout/Empty'

import {
  IControleFornecedorEscopoDiaItem,
  IEscopoDiaPagination
} from '@service/controlefornecedor'

interface IEscopoTableProps {
  data: IControleFornecedorEscopoDiaItem[]
  pagination: IEscopoDiaPagination | null
  currentPage: number
  onPageChange: (page: number) => void
}

// Aceita a descrição com ou sem acento (o backend já mandou das duas formas)
const TIPO_COLORS: Record<string, string> = {
  Regular: 'green',
  Atraso: 'red',
  Pendencia: 'orange',
  Pendência: 'orange'
}

// parseISO trata "yyyy-MM-dd" como data local (new Date() trata como UTC e
// mostrava o dia anterior no Brasil)
function formatDate(dateStr?: string): string {
  if (!dateStr) return '—'
  const d = parseISO(dateStr)
  return isValid(d) ? format(d, 'dd/MM/yyyy') : '—'
}

function renderBoolField(value?: boolean): ReactElement {
  if (value === undefined || value === null) {
    return (
      <Text fontSize="12px" color="gray.400">
        —
      </Text>
    )
  }
  return (
    <Badge
      colorScheme={value ? 'red' : 'green'}
      fontSize="10px"
      borderRadius="full"
      px={2}
    >
      {value ? 'Sim' : 'Não'}
    </Badge>
  )
}

const CompradoresCell: FC<{
  compradores?: string
  compradoresEmails?: string
  quantidade: number
}> = ({ compradores, compradoresEmails, quantidade }) => {
  const nomes = (compradores ?? '')
    .split(',')
    .map(nome => nome.trim())
    .filter(Boolean)
  const emails = (compradoresEmails ?? '').split(',').map(email => email.trim())
  // Usa os nomes recebidos como fonte; quantidade só como fallback
  const total = nomes.length || quantidade

  if (nomes.length === 0) {
    return (
      <Text fontSize="11px" color="gray.400" fontStyle="italic">
        Sem comprador cadastrado
      </Text>
    )
  }

  const MAX_VISIBLE = 2

  if (nomes.length <= MAX_VISIBLE) {
    return (
      <HStack spacing={1} flexWrap="wrap">
        {nomes.map((nome, i) => (
          <Tooltip key={i} label={emails[i] || nome} fontSize="xs">
            <Tag size="sm" colorScheme="blue" borderRadius="full">
              <TagLabel fontSize="10px">{nome}</TagLabel>
            </Tag>
          </Tooltip>
        ))}
      </HStack>
    )
  }

  const tooltipContent = nomes
    .map((nome, i) => `${nome} (${emails[i] || ''})`)
    .join('\n')

  return (
    <HStack spacing={1} flexWrap="wrap">
      <Tooltip label={emails[0] || nomes[0]} fontSize="xs">
        <Tag size="sm" colorScheme="blue" borderRadius="full">
          <TagLabel fontSize="10px">{nomes[0]}</TagLabel>
        </Tag>
      </Tooltip>
      <Tooltip label={tooltipContent} fontSize="xs" whiteSpace="pre-line">
        <Tag size="sm" colorScheme="gray" borderRadius="full" cursor="pointer">
          <TagLabel fontSize="10px">+{total - 1}</TagLabel>
        </Tag>
      </Tooltip>
    </HStack>
  )
}

const EscopoTable: FC<IEscopoTableProps> = ({
  data,
  pagination,
  currentPage,
  onPageChange
}) => {
  if (data.length === 0) {
    return (
      <LayoutEmpty
        title="Nada encontrado!"
        message="Filtre sua busca ao lado para obter informações"
      />
    )
  }

  return (
    <Box>
      {pagination && (
        <Text fontSize="xs" color="gray.500" mb={2} px={2}>
          {pagination.totalRows} registro(s) encontrado(s) — Página{' '}
          {currentPage} de {pagination.totalPages}
        </Text>
      )}

      <Box overflowX="auto">
        <Table size="sm" variant="simple" className="scl-table-grid">
          <Thead bg="#EDF2F7">
            <Tr>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Data
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Tipo
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Empresa
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Centro
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Fornecedor
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Cat. Compacta
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Freq.
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Compradores
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Próx. Compra
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Em Atraso
              </Th>
              <Th fontSize="10px" whiteSpace="nowrap" px={3} py={2}>
                Pendência
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map(item => (
              <Tr
                key={item.id}
                _hover={{ bg: 'gray.50' }}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <Td px={3} py={2}>
                  <Text fontSize="12px">{formatDate(item.data)}</Text>
                </Td>
                <Td px={3} py={2}>
                  <Badge
                    colorScheme={TIPO_COLORS[item.tipoDescricao] || 'gray'}
                    fontSize="10px"
                    borderRadius="full"
                    px={2}
                  >
                    {item.tipoDescricao}
                  </Badge>
                </Td>
                <Td px={3} py={2}>
                  <Text fontSize="12px">{item.empresaNome}</Text>
                  <Text fontSize="10px" color="gray.400">
                    #{item.empresaId}
                  </Text>
                </Td>
                <Td px={3} py={2}>
                  <Text fontSize="12px">{item.lojaNome}</Text>
                  <Text fontSize="10px" color="gray.400">
                    #{item.lojaId}
                  </Text>
                </Td>
                <Td px={3} py={2}>
                  <Text fontSize="12px">{item.fornecedorNome}</Text>
                  <Text fontSize="10px" color="gray.400">
                    #{item.fornecedorId}
                  </Text>
                </Td>
                <Td px={3} py={2}>
                  <Text fontSize="12px">{item.categoriaCompactaNome}</Text>
                  <Text fontSize="10px" color="gray.400">
                    #{item.categoriaCompactaId}
                  </Text>
                </Td>
                <Td px={3} py={2}>
                  <Text fontSize="12px">
                    {item.frequencia != null ? `${item.frequencia} dias` : '—'}
                  </Text>
                </Td>
                <Td px={3} py={2}>
                  <CompradoresCell
                    compradores={item.compradores}
                    compradoresEmails={item.compradoresEmails}
                    quantidade={item.quantidadeCompradores ?? 0}
                  />
                </Td>
                <Td px={3} py={2}>
                  <Text fontSize="12px">
                    {formatDate(item.proximaCompraPrevista)}
                  </Text>
                </Td>
                <Td px={3} py={2}>
                  {renderBoolField(item.emAtraso)}
                </Td>
                <Td px={3} py={2}>
                  {renderBoolField(item.pendencia)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {pagination && pagination.totalPages > 1 && (
        <Box mt={3}>
          <ReactPaginate
            previousLabel="Anterior"
            nextLabel="Próximo"
            pageCount={pagination.totalPages}
            forcePage={currentPage - 1}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            activeClassName="active"
            containerClassName="pagination"
            onPageChange={({ selected }) => onPageChange(selected + 1)}
          />
        </Box>
      )}
    </Box>
  )
}

export default EscopoTable
